import { useEffect, useState } from "react";
import axios from "axios";
import { BarChart3, CalendarClock } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { API } from "../../../config";
import { formatEur } from "../../../pages/SmsCreditPage";

const TooltipBox = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-black border border-gray-700 rounded-lg px-3 py-2 text-xs" data-testid="sms-usage-tooltip">
      <p className="text-white font-semibold mb-1">{label}</p>
      <p className="text-[#ff4d63]">Spesa: {formatEur(d.spend_eur, 3)}</p>
      <p className="text-gray-300">SMS: {d.sms}</p>
      {d.projection_eur > 0 && <p className="text-gray-400 mt-1">Proiezione fine mese: {formatEur(d.spend_eur + d.projection_eur, 3)}</p>}
    </div>
  );
};

const ForecastBadge = ({ fc }) => (
  <span data-testid="sms-forecast-badge"
    className={`flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border ${fc.until ? "border-[#E31837]/50 bg-[#E31837]/10 text-white" : "border-gray-700 text-gray-400"}`}>
    <CalendarClock size={13} />
    {fc.until
      ? <>Credito stimato fino al <b>{new Date(fc.until).toLocaleDateString("it-IT")}</b> · ~{fc.days_left} gg</>
      : "Previsione non disponibile: nessun consumo recente"}
  </span>
);

export const SmsUsageChart = ({ reloadKey }) => {
  const [data, setData] = useState(null);
  const [country, setCountry] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/sms-credit/usage-trend`, { params: country ? { country } : {} })
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [country, reloadKey]);

  if (!data) return null;
  const fc = data.forecast;
  const months = data.months.map((m, i) => (i === data.months.length - 1 && fc.month_extra_eur > 0 ? { ...m, projection_eur: fc.month_extra_eur } : m));
  const totSpend = data.months.reduce((s, m) => s + m.spend_eur, 0);
  const totSms = data.months.reduce((s, m) => s + m.sms, 0);

  return (
    <div className="bg-[#111] border border-gray-800 rounded-2xl p-6" data-testid="sms-usage-chart">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <p className="flex items-center gap-2 text-white font-semibold"><BarChart3 size={18} /> Andamento consumi SMS</p>
          <p className="text-xs text-gray-400 mt-1" data-testid="sms-usage-totals">
            Ultimi 12 mesi · spesa {formatEur(totSpend)} · {totSms.toLocaleString("it-IT")} SMS
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        <ForecastBadge fc={fc} />
        <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="sms-usage-country-select"
          className="bg-[#0a0a0a] border border-gray-700 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-gray-500">
          <option value="">Tutti i paesi</option>
          {data.countries.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        </div>
      </div>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={months} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#1f2937" vertical={false} />
            <XAxis dataKey="label" stroke="#6b7280" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="eur" stroke="#6b7280" tick={{ fontSize: 11 }} tickFormatter={(v) => `€ ${v}`} />
            <YAxis yAxisId="sms" orientation="right" stroke="#6b7280" tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip content={<TooltipBox />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span className="text-gray-300">{v}</span>} />
            <Bar yAxisId="eur" dataKey="spend_eur" name="Spesa €" stackId="s" fill="#E31837" />
            <Bar yAxisId="eur" dataKey="projection_eur" name="Proiezione mese corrente" stackId="s" fill="rgba(227,24,55,0.12)"
              stroke="#E31837" strokeDasharray="5 4" strokeWidth={1.5} radius={[4, 4, 0, 0]} />
            <Line yAxisId="sms" dataKey="sms" name="SMS inviati" stroke="#e5e7eb" strokeWidth={2} dot={{ r: 3 }} type="monotone" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
